/**
 * Department Tabs Component
 *
 * @module @features/departments/components/DepartmentTabs
 */

import React from 'react';
import { Tabs, Tab, TabPanel } from '@dynamon-as/ui';
import { useI18n } from '@shared/i18n';
import type { Employee, OrgEmployee } from '@shared/types';
import styles from '../MyDepartments.module.css';
import { PersonalInfo, CompetenceList, AvailabilityInfo, ShiftsInfo } from './';

/**
 * Props for DepartmentTabs component
 */
export interface DepartmentTabsProps {
    orgEmployee: OrgEmployee;
    selectedDepartmentEmployee: Employee | undefined;
    activeDepartmentTab: string;
    setActiveDepartmentTab: (value: string) => void;
}

/**
 * Displays department tabs with employee information
 */
export function DepartmentTabs({
    orgEmployee,
    selectedDepartmentEmployee,
    activeDepartmentTab,
    setActiveDepartmentTab,
}: DepartmentTabsProps): React.ReactElement {
    const { t } = useI18n();

    return (
        <div className={styles.tabsAndPanels}>
            <div className={styles.tabsContainer}>
                <Tabs
                    value={activeDepartmentTab}
                    onChange={(_, value) =>
                        setActiveDepartmentTab(value as string)
                    }
                    className={styles.tabs}
                >
                    {orgEmployee.employees.map((dept) => (
                        <Tab
                            key={dept.id}
                            value={dept.id}
                            label={
                                dept.departmentName ??
                                t('departments.departmentTabTitle', {
                                    id: dept.id.slice(0, 8),
                                })
                            }
                            className={styles.tab}
                        />
                    ))}
                </Tabs>
            </div>

            {orgEmployee.employees.map((dept) => (
                <TabPanel
                    key={dept.id}
                    index={dept.id}
                    activeIndex={activeDepartmentTab}
                >
                    <div className={styles.tabContent}>
                        <div className={styles.content}>
                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>
                                    {t('departments.sections.personal')}
                                </h2>
                                <PersonalInfo
                                    employee={selectedDepartmentEmployee}
                                />
                            </div>

                            <div className={styles.divider} />

                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>
                                    {t('departments.sections.competence')}
                                </h2>
                                <CompetenceList
                                    employee={selectedDepartmentEmployee}
                                />
                            </div>

                            <div className={styles.divider} />

                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>
                                    {t('departments.sections.shifts')}
                                </h2>
                                <ShiftsInfo
                                    employee={selectedDepartmentEmployee}
                                />
                            </div>

                            <div className={styles.divider} />

                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>
                                    {t('departments.sections.availability')}
                                </h2>
                                <AvailabilityInfo
                                    employee={selectedDepartmentEmployee}
                                />
                            </div>
                        </div>
                    </div>
                </TabPanel>
            ))}
        </div>
    );
}
